import { useState, useEffect } from 'react';

import { ArrowUpDown } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { LabellerFactViewer } from './fact-viewer';
import {
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
} from '@/../../backend/src/matter/inconsistency/inconsistency-display';
import { useCreateInconsistencyFromFacts } from '@/api/mutations/useCreateInconsistencyFromFacts';
import { useGetFact } from '@/api/queries/useGetFact';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Textarea } from '@/new/components/shadcn/textarea';

interface CreateInconsistencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFactIds: Set<string>;
  onSuccess: () => void;
}

interface FactAssignment {
  factId: string;
  groupName: string;
}

const inconsistencyTypes = Object.values(InconsistencyType).map((type) => ({
  value: type,
  label: getInconsistencyTypeDisplayName(type),
}));

const inconsistencySources = Object.values(InconsistencySource).map((source) => ({
  value: source,
  label: getInconsistencySourceDisplayName(source),
}));

const inconsistencyLevels = [
  { value: InconsistencyLevel.Minor, label: 'Minor' },
  { value: InconsistencyLevel.Severe, label: 'Severe' },
  { value: InconsistencyLevel.Critical, label: 'Critical' },
];

const getGroupOptions = (source: InconsistencySource) => {
  switch (source) {
    case InconsistencySource.DepositionVsEvidence:
      return ['Claim', 'Evidence'];
    case InconsistencySource.MultipleDepositions:
      return ['Claim', 'Different Claim'];
    case InconsistencySource.SameDeposition:
      return ['Claim', 'Different Claim'];
    case InconsistencySource.EvidenceVsEvidence:
      return ['Evidence', 'Different Evidence'];
  }
};

export const CreateInconsistencyModal = ({
  isOpen,
  onClose,
  selectedFactIds,
  onSuccess,
}: CreateInconsistencyModalProps) => {
  const { matterId, subsetId } = useParams();
  const factIdsArray = Array.from(selectedFactIds);
  const totalCount = factIdsArray.length;

  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const currentFactId = factIdsArray[currentFactIndex] ?? '';
  const {
    data: currentFact,
    isLoading: isFactLoading,
    isError: isFactError,
  } = useGetFact(matterId ?? '', currentFactId);

  const [factAssignments, setFactAssignments] = useState<FactAssignment[]>([]);
  const [description, setDescription] = useState('');
  const [type, setType] = useState<InconsistencyType>(InconsistencyType.Events);
  const [source, setSource] = useState<InconsistencySource>(InconsistencySource.DepositionVsEvidence);
  const [level, setLevel] = useState<InconsistencyLevel>(InconsistencyLevel.Severe);

  useEffect(() => {
    if (isOpen && factIdsArray.length > 0) {
      setCurrentFactIndex(0);
      setDescription('');
      setType(InconsistencyType.Events);
      setSource(InconsistencySource.DepositionVsEvidence);
      setLevel(InconsistencyLevel.Severe);

      const groupOptions = getGroupOptions(InconsistencySource.DepositionVsEvidence);
      setFactAssignments(factIdsArray.map((id) => ({ factId: id, groupName: groupOptions[0] })));
    }
    // factIdsArray is derived fresh each render — key the effect on a stable signature.
  }, [isOpen, factIdsArray.join(',')]);

  // Reset assignments when source changes
  useEffect(() => {
    if (factAssignments.length > 0) {
      const groupOptions = getGroupOptions(source);
      setFactAssignments((prev) => prev.map((assignment) => ({ ...assignment, groupName: groupOptions[0] })));
    }
  }, [source]);

  const { mutate: createInconsistency, isLoading } = useCreateInconsistencyFromFacts(matterId ?? '', subsetId ?? '', {
    onSuccess: () => {
      toast.success('Inconsistency created successfully');
      onSuccess();
      onClose();
    },
  });

  const groupOptions = getGroupOptions(source);
  const group1Assignments = factAssignments.filter((a) => a.groupName === groupOptions[0]);
  const group2Assignments = factAssignments.filter((a) => a.groupName === groupOptions[1]);

  const handleCreate = () => {
    if (!description.trim() || factAssignments.length === 0) return;
    if (group1Assignments.length === 0 || group2Assignments.length === 0) return;

    createInconsistency({ factAssignments, type, source, level, description });
  };

  const updateFactAssignment = (factId: string, groupName: string) => {
    setFactAssignments((prev) =>
      prev.map((assignment) => (assignment.factId === factId ? { ...assignment, groupName } : assignment)),
    );
  };

  const currentFactAssignment = factAssignments.find((a) => a.factId === currentFactId);

  const handlePrevFact = () => setCurrentFactIndex((prev) => Math.max(0, prev - 1));
  const handleNextFact = () => setCurrentFactIndex((prev) => Math.min(totalCount - 1, prev + 1));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-4rem)]">
        <div className="flex max-h-[70vh]">
          <LabellerFactViewer
            currentFact={currentFact}
            currentFactIndex={currentFactIndex}
            total={totalCount}
            isLoading={isFactLoading}
            isError={isFactError}
            onPrevious={handlePrevFact}
            onNext={handleNextFact}
          >
            {currentFactId && (
              <div>
                <Label>Assign to Group</Label>
                <Select
                  value={currentFactAssignment?.groupName || groupOptions[0]}
                  onValueChange={(value) => updateFactAssignment(currentFactId, value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {groupOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </LabellerFactViewer>

          <div className="w-1/2 overflow-y-auto p-6">
            <h3 className="mb-4 text-lg font-semibold">Create Inconsistency</h3>
            <div className="mb-4 flex gap-2.5 rounded-md border border-gray-300 bg-gray-50 px-4 py-3 text-gray-600">
              <Icon className="lucide--info mt-0.75" />
              <p>
                The inconsistency will appear in the inconsistencies list immediately, but the linked facts may take up
                to ~1 minute to show as disputed in the facts table while OpenSearch catches up.
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select value={type} onValueChange={(value) => setType(value as InconsistencyType)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencyTypes.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="source">Source</Label>
                  <Select value={source} onValueChange={(value) => setSource(value as InconsistencySource)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencySources.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="level">Severity</Label>
                  <Select value={level} onValueChange={(value) => setLevel(value as InconsistencyLevel)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {inconsistencyLevels.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the inconsistency"
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="mt-6 border-t pt-4">
                {factAssignments.length > 0 ? (
                  <div className="space-y-3">
                    <div className="rounded-lg bg-blue-50/50 p-3 text-sm font-medium text-blue-900">
                      {groupOptions[0]} ({group1Assignments.length} facts)
                    </div>

                    <div className="flex justify-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-gray-100">
                        <ArrowUpDown className="h-4 w-4 text-gray-600" />
                      </div>
                    </div>

                    <div className="rounded-lg bg-red-50/50 p-3 text-sm font-medium text-red-900">
                      {groupOptions[1]} ({group2Assignments.length} facts)
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Loading preview...</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={
              !description.trim() || isLoading || group1Assignments.length === 0 || group2Assignments.length === 0
            }
          >
            {isLoading ? 'Creating...' : 'Create Inconsistency'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
